import { useEffect, useState } from "react";
import api from "@/lib/api";
import Header from "@/components/Header";
import { toast } from "sonner";
import { Save, Trash2, Plus, Upload, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const SPORTS = [
  { value: "Lacrosse", enabled: true },
  { value: "Football", enabled: false },
  { value: "Basketball", enabled: false },
  { value: "Baseball", enabled: false },
  { value: "Soccer", enabled: false },
  { value: "Volleyball", enabled: false },
];
const GENDERS = ["Men", "Women", "Coed"];
const LEVELS = ["MS", "HS", "JUCO", "College"];
const DIVISIONS = ["D1", "D2", "D3", "NAIA", "NJCAA", "MCLA D1", "MCLA D2", "WCLA", "NCLA", "Club", "HS Varsity", "JV", "Freshman", "Middle School"];
const ROLES = ["Head Coach", "Associate Head Coach", "Assistant Coach", "Offensive Coordinator", "Defensive Coordinator", "Director of Operations", "Graduate Assistant", "Volunteer Assistant"];
const POSITIONS = ["Attack", "Midfield", "Defense", "LSM", "Faceoff", "Goalie"];
const MENTOR_TIERS = ["elite", "national", "regional", "developmental"];
const MENTOR_LEVELS = ["D1", "D2", "D3", "NAIA", "MCLA", "HS", "Club"];
const REGIONS = ["Northeast", "Southeast", "Upper Midwest", "Lower Midwest", "Northwest", "Southwest"];
const SETTINGS = ["Public", "Private"];
const SELECTIVITY = ["Most Selective", "More Selective", "Less Selective", "Least Selective"];
const ENROLLMENT = ["<3,000", "3,000-10,000", "10,000-20,000", ">20,000"];
const SCHOLARSHIPS = ["Full", "Ivy (Need-based)", "Partial", "None"];
const SALARY_BANDS = ["0-5,000", "5,000-10,000", "10,000-25,000", "25,000-50,000", "50,000-75,000", "75,000-100,000", "100,000-200,000", "200,000+"];
const AFFILIATIONS = ["USILA", "NILCA", "IMLCA", "IWLCA", "USL"];

export default function CoachProfileEdit() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracted, setExtracted] = useState(null);

  useEffect(() => {
    api.get("/coach/profile").then(({ data }) => setP(data));
  }, []);

  if (!p) return <Shell><div className="p-10 heading text-3xl">LOADING…</div></Shell>;

  const u = (key, value) => setP({ ...p, [key]: value });
  const uNested = (key, sub, value) => setP({ ...p, [key]: { ...(p[key] || {}), [sub]: value } });
  const toggleArr = (key, value) => {
    const arr = p[key] || [];
    u(key, arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        headline: p.headline || "",
        phone: p.phone || null,
        sms_opt_in: !!p.sms_opt_in,
        philosophy: p.philosophy || "",
        sport: p.sport || "Lacrosse",
        gender: p.gender || "Men",
        primary_level: p.primary_level || "College",
        desired_role: (p.desired_roles && p.desired_roles[0]) || p.desired_role || "Head Coach",
        desired_roles: p.desired_roles || (p.desired_role ? [p.desired_role] : []),
        desired_divisions: p.desired_divisions || [],
        open_to_hs_jobs: p.open_to_hs_jobs || false,
        open_to_college_jobs: p.open_to_college_jobs || false,
        desired_salary_band: p.desired_salary_band || null,
        geographic_regions: p.geographic_regions || [],
        program_type_preferences: p.program_type_preferences || [],
        setting_preferences: p.setting_preferences || [],
        selectivity_preferences: p.selectivity_preferences || [],
        enrollment_preferences: p.enrollment_preferences || [],
        scholarship_preferences: p.scholarship_preferences || [],
        facilities_required: p.facilities_required || {},
        camps_clinics: p.camps_clinics || {},
        location: p.location || "",
        aspirations: p.aspirations || "",
        playing_history: p.playing_history || {},
        education: p.education || {},
        coaching_history: p.coaching_history || [],
        club_experience: p.club_experience || [],
        affiliations: p.affiliations || [],
        coach_hof_memberships: p.coach_hof_memberships || [],
        mentors: p.mentors || [],
        maxpreps_url: p.maxpreps_url || null,
        specialties: p.specialties || [],
        departure_conditions: p.departure_conditions || "",
        position_type_pref: p.position_type_pref || "either",
        open_to_faculty: p.open_to_faculty || false,
        faculty_subjects: p.faculty_subjects || [],
        privacy: p.privacy || { discoverable: true, anonymous_in_pool: true, mask_current_employer: true, hidden_from_programs: [], hidden_from_program_names: [] },
        hc_searcher: p.hc_searcher || { enabled: false, program_name: "", level: "College", division: "D1", location: "" },
        facility_priorities: p.facility_priorities || { practice_field_turf: 0, practice_field_grass: 0, game_field_turf: 0, game_field_grass: 0, indoor_facility: 0, weight_room: 0, locker_room: 0, other_facilities: 0 },
      };
      const { data } = await api.put("/coach/profile", payload);
      setP(data);
      toast.success("Saved. Your progress is stored — keep going or come back anytime.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const addStint = () => u("coaching_history", [...(p.coaching_history || []), {
    role: "Head Coach", organization: "", sport: p.sport, gender: p.gender, level: p.primary_level,
    division: "D1", conference: "", start_year: new Date().getFullYear(), end_year: null,
    wins: 0, losses: 0, championships: [], accolades: [], notes: "",
  }]);
  const updStint = (i, k, v) => {
    const arr = [...p.coaching_history];
    arr[i] = { ...arr[i], [k]: v };
    u("coaching_history", arr);
  };
  const delStint = (i) => u("coaching_history", p.coaching_history.filter((_, j) => j !== i));

  // Mentor Tree helpers (cap at 10)
  const addMentor = () => {
    if ((p.mentors || []).length >= 10) return;
    u("mentors", [...(p.mentors || []), {
      name: "", role: "Head Coach", organization: "", level: "", tier: "",
      championships: 0, years_under: null, hof: false, national_champion: false, as_assistant: false,
    }]);
  };
  const updMentor = (i, k, v) => {
    const arr = [...(p.mentors || [])];
    arr[i] = { ...arr[i], [k]: v };
    u("mentors", arr);
  };
  const delMentor = (i) => u("mentors", (p.mentors || []).filter((_, j) => j !== i));

  const onResumeFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset so same file can be re-uploaded
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Resume too large (8 MB max)"); return; }
    setUploading(true);
    setExtracted(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/coach/profile/import-resume", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setExtracted(data);
      toast.success(`CoachProfileAgent extracted ${Object.keys(data.extracted || {}).length} sections.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resume extraction failed");
    } finally { setUploading(false); }
  };

  const mergeExtracted = () => {
    if (!extracted?.extracted) return;
    const ex = extracted.extracted;
    const merged = { ...p };
    // Top-level scalars/lists — only overwrite when extracted has a value
    for (const k of ["headline", "philosophy", "sport", "gender", "primary_level", "desired_role", "location", "affiliations", "coach_hof_memberships", "mentors"]) {
      if (ex[k] !== undefined && ex[k] !== null && ex[k] !== "") merged[k] = ex[k];
    }
    // Deep-merge nested objects
    if (ex.playing_history) merged.playing_history = { ...(p.playing_history || {}), ...ex.playing_history };
    if (ex.education) merged.education = { ...(p.education || {}), ...ex.education };
    if (Array.isArray(ex.coaching_history) && ex.coaching_history.length) {
      merged.coaching_history = [...(p.coaching_history || []), ...ex.coaching_history];
    }
    setP(merged);
    setExtracted(null);
    toast.success("Merged. Review and click SAVE to commit.");
  };

  return (
    <Shell>
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-6xl" data-testid="coach-profile-edit">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs tracking-widest text-blaze">PROFILE BUILDER</div>
            <h1 className="heading text-4xl md:text-5xl mt-2">YOUR CAREER GRAPH</h1>
          </div>
          <button onClick={save} disabled={busy} data-testid="save-profile-btn" className="bg-blaze text-white heading text-lg px-6 py-3 min-h-[48px] hover:glow-blaze transition-all disabled:opacity-50 flex items-center gap-2">
            <Save size={16} /> {busy ? "SAVING…" : "SAVE & MATCH"}
          </button>
        </div>

        <div data-testid="save-as-you-go-banner" className="mt-5 border border-volt/40 bg-volt/5 p-4 flex items-start gap-3">
          <Save size={16} className="text-volt mt-0.5 shrink-0" />
          <p className="text-sm text-neutral-300 leading-relaxed">
            <span className="text-volt heading text-xs tracking-widest mr-2">SAVE AS YOU GO</span>
            You don't have to finish this in one sitting. Hit <span className="text-white font-semibold">SAVE</span> anytime — your progress is stored and you can pick up right where you left off in your next session.
          </p>
        </div>

        <Section title="BOOTSTRAP FROM RESUME (OPTIONAL)">
          <p className="text-xs text-neutral-500 -mt-2">CoachProfileAgent will read your PDF / DOCX and pre-fill the fields below. You always review before saving.</p>
          <div className="flex flex-wrap items-center gap-3">
            <label data-testid="resume-upload-label" className="inline-flex items-center gap-2 min-h-[44px] px-4 border border-volt text-volt heading text-sm tracking-widest cursor-pointer hover:bg-volt hover:text-white transition-colors">
              <Upload size={14} /> {uploading ? "EXTRACTING…" : "UPLOAD RESUME (PDF / DOCX)"}
              <input data-testid="resume-file-input" type="file" accept=".pdf,.docx,.txt" disabled={uploading} onChange={onResumeFile} className="hidden" />
            </label>
            {extracted && (
              <button
                data-testid="merge-extracted-btn"
                onClick={mergeExtracted}
                className="inline-flex items-center gap-2 min-h-[44px] px-4 bg-volt text-white heading text-sm tracking-widest hover:glow-volt transition-all"
              >
                <Sparkles size={14} /> MERGE INTO PROFILE (preview CIR {extracted.preview_qbr?.score ?? "—"})
              </button>
            )}
          </div>
          {extracted && (
            <pre data-testid="extracted-preview" className="mt-3 max-h-60 overflow-auto bg-[#070707] border border-neutral-800 text-[11px] text-neutral-300 p-3 whitespace-pre-wrap">
{JSON.stringify(extracted.extracted, null, 2)}
            </pre>
          )}
        </Section>

        <Section title="IDENTITY" hint="Who you are as a coach — your headline, philosophy, and where you're based. This is the first thing programs see.">
          <Grid cols={2}>
            <Field label="Headline"><input className="input" data-testid="field-headline" value={p.headline || ""} onChange={(e) => u("headline", e.target.value)} placeholder="Possession-first defensive mind…" /></Field>
            <Field label="Location"><input className="input" data-testid="field-location" value={p.location || ""} onChange={(e) => u("location", e.target.value)} placeholder="Baltimore, MD" /></Field>
          </Grid>
          <Grid cols={2}>
            <Field label="Cell Phone (never shown publicly)">
              <input className="input" type="tel" data-testid="field-phone" value={p.phone || ""} onChange={(e) => u("phone", e.target.value)} placeholder="(555) 123-4567" />
            </Field>
            <label className="flex items-center gap-3 mt-6 cursor-pointer" data-testid="field-sms-opt-in">
              <input type="checkbox" className="accent-[#FF3B30] w-4 h-4" checked={!!p.sms_opt_in} onChange={(e) => u("sms_opt_in", e.target.checked)} />
              <span className="text-xs text-neutral-300 leading-snug">Text me about priority matches & time-sensitive openings (opt out anytime)</span>
            </label>
          </Grid>
          <Field label="Coaching Philosophy"><textarea className="input min-h-[100px]" data-testid="field-philosophy" value={p.philosophy || ""} onChange={(e) => u("philosophy", e.target.value)} placeholder="Disciplined zone-and-man hybrid…" /></Field>
          <Field label="Aspirations"><textarea className="input min-h-[70px]" data-testid="field-aspirations" value={p.aspirations || ""} onChange={(e) => u("aspirations", e.target.value)} /></Field>
        </Section>

        <Section title="MY PREFERENCES" hint="These are the job characteristics I'd need to consider moving — role, division, salary, geography, and school type. We only match you against openings that fit what you tell us here.">
          <Grid cols={4}>
            <Field label="Sport">
              <select
                className="input"
                data-testid="field-sport"
                value={p.sport || "Lacrosse"}
                onChange={(e) => u("sport", e.target.value)}
              >
                {SPORTS.map((s) => (
                  <option key={s.value} value={s.value} disabled={!s.enabled}>
                    {s.value}{s.enabled ? "" : " — coming soon"}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Gender"><select className="input" data-testid="field-gender" value={p.gender || "Men"} onChange={(e) => u("gender", e.target.value)}>{GENDERS.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Primary Level"><select className="input" data-testid="field-primary-level" value={p.primary_level || "College"} onChange={(e) => u("primary_level", e.target.value)}>{LEVELS.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Desired Role · multi-select">
              <ChipRow
                options={ROLES}
                value={p.desired_roles && p.desired_roles.length ? p.desired_roles : (p.desired_role ? [p.desired_role] : [])}
                onToggle={(v) => {
                  const cur = p.desired_roles && p.desired_roles.length ? p.desired_roles : (p.desired_role ? [p.desired_role] : []);
                  const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
                  setP({ ...p, desired_roles: next, desired_role: next[0] || "Head Coach" });
                }}
                testid="chip-desired-role"
              />
            </Field>
          </Grid>
          <Field label="Desired Divisions">
            <ChipRow options={DIVISIONS} value={p.desired_divisions || []} onToggle={(v) => toggleArr("desired_divisions", v)} testid="chip-division" />
          </Field>
          {/* Cross-level willingness — signals whether the coach wants pitches
              outside their primary level. Defaults off so we don't spam. */}
          <Field label="Cross-Level Willingness">
            <div className="flex flex-wrap gap-4 text-sm">
              {p.primary_level === "College" || p.primary_level === "College Club" ? (
                <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-open-hs">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-blaze"
                    checked={!!p.open_to_hs_jobs}
                    onChange={(e) => u("open_to_hs_jobs", e.target.checked)}
                  />
                  <span className="text-neutral-300">I&apos;ll consider <b className="text-white">HS varsity</b> openings</span>
                </label>
              ) : null}
              {p.primary_level === "HS" || p.primary_level === "MS" ? (
                <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-open-college">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-blaze"
                    checked={!!p.open_to_college_jobs}
                    onChange={(e) => u("open_to_college_jobs", e.target.checked)}
                  />
                  <span className="text-neutral-300">I&apos;ll consider <b className="text-white">college / D1-D3 / MCLA</b> openings</span>
                </label>
              ) : null}
              <div className="text-[10px] tracking-widest text-neutral-500 w-full">
                Off by default. Toggle on to appear in ADs&apos; searches at the other level.
              </div>
            </div>
          </Field>
          <Field label="Commitment — are you open to part-time / stipend roles?">
            <div className="flex flex-wrap gap-2" data-testid="position-type-tags">
              {[
                { v: "full_time", label: "Full-time only" },
                { v: "part_time", label: "Part-time (stipend) only" },
                { v: "either", label: "Either — full or part-time" },
              ].map((opt) => {
                const on = (p.position_type_pref || "either") === opt.v;
                return (
                  <button
                    type="button"
                    key={opt.v}
                    data-testid={`position-type-${opt.v}`}
                    onClick={() => u("position_type_pref", opt.v)}
                    className={`px-3 min-h-[36px] text-xs tracking-widest border transition-colors ${on ? "bg-volt text-white border-volt" : "border-neutral-700 text-neutral-300 hover:border-volt"}`}
                  >
                    {opt.label.toUpperCase()}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] tracking-widest text-neutral-500 mt-1">Part-time HS varsity roles usually pay a seasonal stipend — a great fit alongside a day job. ADs filter on this.</div>
          </Field>
          <Field label="Salary Band">
            <ChipRow options={SALARY_BANDS} value={p.desired_salary_band ? [p.desired_salary_band] : []} onToggle={(v) => u("desired_salary_band", p.desired_salary_band === v ? null : v)} testid="chip-salary" single />
          </Field>
          <Field label="Geographic Regions">
            <ChipRow options={REGIONS} value={p.geographic_regions || []} onToggle={(v) => toggleArr("geographic_regions", v)} testid="chip-region" />
          </Field>
          <Grid cols={3}>
            <Field label="Setting"><ChipRow options={SETTINGS} value={p.setting_preferences || []} onToggle={(v) => toggleArr("setting_preferences", v)} testid="chip-setting" /></Field>
            <Field label="School Academic Selectivity (acceptance rate + academic standards)"><ChipRow options={SELECTIVITY} value={p.selectivity_preferences || []} onToggle={(v) => toggleArr("selectivity_preferences", v)} testid="chip-selectivity" /></Field>
            <Field label="Enrollment"><ChipRow options={ENROLLMENT} value={p.enrollment_preferences || []} onToggle={(v) => toggleArr("enrollment_preferences", v)} testid="chip-enrollment" /></Field>
          </Grid>
          <Field label="Scholarships"><ChipRow options={SCHOLARSHIPS} value={p.scholarship_preferences || []} onToggle={(v) => toggleArr("scholarship_preferences", v)} testid="chip-scholarship" /></Field>
          <Field label="Affiliations"><ChipRow options={AFFILIATIONS} value={p.affiliations || []} onToggle={(v) => toggleArr("affiliations", v)} testid="chip-affiliation" /></Field>
        </Section>

        <Section title="PLAYING HISTORY" hint="Your playing history — where you played, who coached you, and what you achieved on the field. This anchors your career graph and connects you to the coaching network you came up through.">
          <p className="text-xs text-neutral-500 -mt-2">
            Playing for a lacrosse hotbed school or elite HS club program gives a meaningful CIR boost in your{" "}
            <Link to="/cir" className="text-volt underline">Playing Pedigree</Link> component.
          </p>
          <Grid cols={2}>
            <Field label="HS Team"><input className="input" data-testid="field-hs-team" value={p.playing_history?.hs_team || ""} onChange={(e) => uNested("playing_history", "hs_team", e.target.value)} placeholder="Garden City HS" /></Field>
            <Field label="HS Location (City, State)"><input className="input" data-testid="field-hs-location" value={p.playing_history?.hs_location || ""} onChange={(e) => uNested("playing_history", "hs_location", e.target.value)} placeholder="Baltimore, MD" /></Field>
            <Field label="HS State (2-letter)"><input className="input" data-testid="field-hs-state" value={p.playing_history?.hs_state || ""} onChange={(e) => uNested("playing_history", "hs_state", e.target.value.toUpperCase().slice(0, 2))} placeholder="MD" maxLength={2} /></Field>
            <Field label="Years Played (Varsity)"><input className="input" type="number" min="0" max="6" data-testid="field-hs-years" value={p.playing_history?.hs_years_played ?? ""} onChange={(e) => uNested("playing_history", "hs_years_played", e.target.value ? parseInt(e.target.value) : null)} /></Field>
            <Field label="HS Position">
              <select className="input" data-testid="field-hs-position" value={p.playing_history?.hs_position || ""} onChange={(e) => uNested("playing_history", "hs_position", e.target.value)}>
                <option value="">—</option>
                {POSITIONS.map((pos) => <option key={pos} value={pos}>{pos}</option>)}
              </select>
            </Field>
            <Field label="HS Coach (their name)"><input className="input" data-testid="field-hs-coach-name" value={p.playing_history?.hs_coach || ""} onChange={(e) => uNested("playing_history", "hs_coach", e.target.value)} placeholder="e.g. Bob Shriver" /></Field>
            <Field label="HS Coach is HOF"><Toggle on={!!p.playing_history?.hs_coach_hof} onChange={(v) => uNested("playing_history", "hs_coach_hof", v)} testid="toggle-hs-coach-hof" /></Field>
            <Field label="HS Club Program (the team you played for outside of HS)"><input className="input" data-testid="field-hs-club-team" value={p.playing_history?.hs_club_team || ""} onChange={(e) => uNested("playing_history", "hs_club_team", e.target.value)} placeholder="Club program name" /></Field>
            <Field label="HS Club Coach"><input className="input" data-testid="field-hs-club-coach" value={p.playing_history?.hs_club_coach || ""} onChange={(e) => uNested("playing_history", "hs_club_coach", e.target.value)} placeholder="e.g. Brian Kelly" /></Field>
            <Field label="HS Club Coach is HOF"><Toggle on={!!p.playing_history?.hs_club_coach_hof} onChange={(v) => uNested("playing_history", "hs_club_coach_hof", v)} testid="toggle-hs-club-coach-hof" /></Field>
            <Field label="HS Captain"><Toggle on={!!p.playing_history?.hs_captain} onChange={(v) => uNested("playing_history", "hs_captain", v)} testid="toggle-hs-captain" /></Field>
            <Field label="HS Elite Team (MaxPreps-tier)"><Toggle on={!!p.playing_history?.hs_elite_team} onChange={(v) => uNested("playing_history", "hs_elite_team", v)} testid="toggle-hs-elite" /></Field>
            <Field label="HS All-American"><Toggle on={!!p.playing_history?.hs_all_american} onChange={(v) => uNested("playing_history", "hs_all_american", v)} testid="toggle-hs-aa" /></Field>
            <Field label="HS / State HOF as Player"><Toggle on={!!p.playing_history?.hs_hof} onChange={(v) => uNested("playing_history", "hs_hof", v)} testid="toggle-hs-hof" /></Field>
            <Field label="College Team"><input className="input" data-testid="field-college-team" value={p.playing_history?.college_team || ""} onChange={(e) => uNested("playing_history", "college_team", e.target.value)} /></Field>
            <Field label="College Coach"><input className="input" data-testid="field-college-coach" value={p.playing_history?.college_coach || ""} onChange={(e) => uNested("playing_history", "college_coach", e.target.value)} placeholder="e.g. Charley Toomey" /></Field>
            <Field label="College Division">
              <select className="input" data-testid="field-college-div" value={p.playing_history?.college_division || ""} onChange={(e) => uNested("playing_history", "college_division", e.target.value)}>
                <option value="">—</option>
                {["D1", "D2", "D3", "NAIA", "NCLA"].map((d) => <option key={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="College Position">
              <select className="input" data-testid="field-college-position" value={p.playing_history?.college_position || ""} onChange={(e) => uNested("playing_history", "college_position", e.target.value)}>
                <option value="">—</option>
                {POSITIONS.map((pos) => <option key={pos} value={pos}>{pos}</option>)}
              </select>
            </Field>
            <Field label="College Captain"><Toggle on={!!p.playing_history?.college_captain} onChange={(v) => uNested("playing_history", "college_captain", v)} testid="toggle-college-captain" /></Field>
            <Field label="College All-American"><Toggle on={!!p.playing_history?.college_all_american} onChange={(v) => uNested("playing_history", "college_all_american", v)} testid="toggle-college-aa" /></Field>
            <Field label="College HOF"><Toggle on={!!p.playing_history?.college_hof} onChange={(v) => uNested("playing_history", "college_hof", v)} testid="toggle-college-hof" /></Field>
            <Field label="National Position Rank (e.g. 14)"><input className="input" type="number" min="1" max="500" data-testid="field-position-rank" value={p.playing_history?.national_position_rank ?? ""} onChange={(e) => uNested("playing_history", "national_position_rank", e.target.value ? parseInt(e.target.value) : null)} /></Field>
            <Field label="Professional Player"><Toggle on={!!p.playing_history?.professional} onChange={(v) => uNested("playing_history", "professional", v)} testid="toggle-pro" /></Field>
            <Field label="Professional Team"><input className="input" data-testid="field-pro-team" value={p.playing_history?.professional_team || ""} onChange={(e) => uNested("playing_history", "professional_team", e.target.value)} placeholder="e.g. Chesapeake Bayhawks (PLL)" /></Field>
          </Grid>
          <Field label="Professional Accolades (one per line)">
            <textarea className="input min-h-[60px]" data-testid="field-pro-accolades" value={(p.playing_history?.professional_accolades || []).join("\n")} onChange={(e) => uNested("playing_history", "professional_accolades", e.target.value.split("\n").filter(Boolean))} />
          </Field>
        </Section>

        <Section title="EDUCATION">
          <Grid cols={3}>
            <Field label="Degree"><input className="input" data-testid="field-degree" value={p.education?.degree || ""} onChange={(e) => uNested("education", "degree", e.target.value)} placeholder="BA Economics" /></Field>
            <Field label="School"><input className="input" data-testid="field-school" value={p.education?.school || ""} onChange={(e) => uNested("education", "school", e.target.value)} /></Field>
            <Field label="GPA"><input className="input" data-testid="field-gpa" type="number" step="0.1" min="0" max="4" value={p.education?.gpa ?? ""} onChange={(e) => uNested("education", "gpa", e.target.value ? parseFloat(e.target.value) : null)} /></Field>
            <Field label="Masters"><Toggle on={!!p.education?.masters} onChange={(v) => uNested("education", "masters", v)} testid="toggle-masters" /></Field>
            <Field label="Masters School"><input className="input" data-testid="field-masters-school" value={p.education?.masters_school || ""} onChange={(e) => uNested("education", "masters_school", e.target.value)} /></Field>
          </Grid>
        </Section>

        <Section title="COACHING HISTORY" hint="Your coaching history — every stint, record, and championship. This is the backbone of your Coach Impact Rating (CIR) and what programs weigh most when evaluating you.">
          <div className="space-y-3">
            {(p.coaching_history || []).map((s, i) => (
              <div key={i} className="border border-neutral-800 p-4 bg-appcard" data-testid={`coaching-stint-${i}`}>
                <Grid cols={4}>
                  <Field label="Role">
                    <select className="input" value={s.role} onChange={(e) => updStint(i, "role", e.target.value)}>{ROLES.map((r) => <option key={r}>{r}</option>)}</select>
                  </Field>
                  <Field label="Assistant Position (if asst)">
                    <select className="input" value={s.assistant_position || ""} onChange={(e) => updStint(i, "assistant_position", e.target.value || null)}>
                      <option value="">—</option>
                      {["OC", "DC", "Ops", "VA", "Position Coach", "Goalies", "FOGOs"].map((r) => <option key={r}>{r}</option>)}
                    </select>
                  </Field>
                  <Field label="Organization"><input className="input" value={s.organization} onChange={(e) => updStint(i, "organization", e.target.value)} /></Field>
                  <Field label="Head Coach (you served under)"><input className="input" value={s.head_coach_name || ""} onChange={(e) => updStint(i, "head_coach_name", e.target.value)} placeholder="e.g. Charley Toomey" /></Field>
                  <Field label="Division">
                    <select className="input" value={s.division || ""} onChange={(e) => updStint(i, "division", e.target.value)}>
                      <option value="">—</option>{DIVISIONS.map((d) => <option key={d}>{d}</option>)}
                    </select>
                  </Field>
                  <Field label="Conference"><input className="input" value={s.conference || ""} onChange={(e) => updStint(i, "conference", e.target.value)} /></Field>
                  <Field label="Start year"><input className="input" type="number" value={s.start_year || ""} onChange={(e) => updStint(i, "start_year", parseInt(e.target.value || 0))} /></Field>
                  <Field label="End year (blank = current)"><input className="input" type="number" value={s.end_year ?? ""} onChange={(e) => updStint(i, "end_year", e.target.value ? parseInt(e.target.value) : null)} /></Field>
                  <Field label="Wins"><input className="input" type="number" value={s.wins ?? 0} onChange={(e) => updStint(i, "wins", parseInt(e.target.value || 0))} /></Field>
                  <Field label="Losses"><input className="input" type="number" value={s.losses ?? 0} onChange={(e) => updStint(i, "losses", parseInt(e.target.value || 0))} /></Field>
                </Grid>
                <Field label="Championships (one per line)">
                  <textarea className="input min-h-[60px]" value={(s.championships || []).join("\n")} onChange={(e) => updStint(i, "championships", e.target.value.split("\n").filter(Boolean))} />
                </Field>
                <Grid cols={2}>
                  <Field label="Team accolades (one per line)">
                    <textarea className="input min-h-[60px]" data-testid={`stint-${i}-team-accolades`}
                      value={(s.team_accolades || []).join("\n")}
                      onChange={(e) => updStint(i, "team_accolades", e.target.value.split("\n").filter(Boolean))}
                      placeholder="e.g. Conference Champions 2022&#10;NCAA Tournament Quarterfinals"
                    />
                  </Field>
                  <Field label="Personal accolades (one per line)">
                    <textarea className="input min-h-[60px]" data-testid={`stint-${i}-personal-accolades`}
                      value={(s.personal_accolades || []).join("\n")}
                      onChange={(e) => updStint(i, "personal_accolades", e.target.value.split("\n").filter(Boolean))}
                      placeholder="e.g. Conference Coach of the Year 2022"
                    />
                  </Field>
                </Grid>

                <SeasonRecordsEditor
                  stintIdx={i}
                  seasons={s.seasons || []}
                  onChange={(arr) => updStint(i, "seasons", arr)}
                />

                <NotablePlayersEditor
                  stintIdx={i}
                  players={s.notable_players || []}
                  onChange={(arr) => updStint(i, "notable_players", arr)}
                />

                <Field label="Legacy accolades (kept for backward compat — prefer team/personal above)">
                  <textarea className="input min-h-[40px] opacity-70" value={(s.accolades || []).join("\n")} onChange={(e) => updStint(i, "accolades", e.target.value.split("\n").filter(Boolean))} />
                </Field>
                <button onClick={() => delStint(i)} data-testid={`del-stint-${i}`} className="mt-2 flex items-center gap-1 text-xs text-blaze hover:text-red-400 uppercase tracking-widest">
                  <Trash2 size={14} /> Remove
                </button>
              </div>
            ))}
            <button onClick={addStint} data-testid="add-stint-btn" className="w-full border border-dashed border-neutral-700 py-3 heading text-base uppercase tracking-widest text-volt hover:bg-volt/10 transition-colors flex items-center justify-center gap-2">
              <Plus size={16} /> Add coaching stint
            </button>
          </div>
        </Section>

        <Section title="PRIVACY & ANONYMITY">
          <p className="text-xs text-neutral-500 -mt-2">Your current employer and other programs you list below will never see you on this platform. Identity is masked until YOU click &quot;Express Interest&quot; on a specific job.</p>
          <Grid cols={2}>
            <Field label="Discoverable in Talent Pool"><Toggle on={p.privacy?.discoverable !== false} onChange={(v) => uNested("privacy", "discoverable", v)} testid="toggle-discoverable" /></Field>
            <Field label="Mask my name (anonymous in pool)"><Toggle on={p.privacy?.anonymous_in_pool !== false} onChange={(v) => uNested("privacy", "anonymous_in_pool", v)} testid="toggle-anonymous-in-pool" /></Field>
            <Field label="Hide current employer until I reveal"><Toggle on={p.privacy?.mask_current_employer !== false} onChange={(v) => uNested("privacy", "mask_current_employer", v)} testid="toggle-mask-current-employer" /></Field>
          </Grid>
          <Field label="Block these specific programs (one per line — usually your current employer + any rivals)">
            <textarea
              className="input min-h-[70px]"
              data-testid="field-hidden-from-program-names"
              value={(p.privacy?.hidden_from_program_names || []).join("\n")}
              onChange={(e) => uNested("privacy", "hidden_from_program_names", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
              placeholder="Loyola Maryland\nNotre Dame Lacrosse Club"
            />
          </Field>
        </Section>

        <Section title="MENTOR TREE / COACHING PEDIGREE (up to 10)" hint="Building my coaching tree — the coaches and mentors who shaped how you coach. Your tree links you into the sport's knowledge graph and surfaces warm connections to hiring programs.">
          <p className="text-xs text-neutral-500 -mt-2">
            List the head coaches you served under (most impactful) and anyone you consider a
            coaching mentor. Mark HOF / national-champion status, championships won, program tier,
            and whether you were their direct assistant. This is the single biggest CIR lever after
            your own record.{" "}
            <Link to="/cir" className="text-volt underline">How CIR works →</Link>
          </p>
          <div className="space-y-3" data-testid="mentor-tree-list">
            {((p.mentors || [])).map((m, i) => (
              <div key={i} className="border border-neutral-800 p-4 bg-appcard" data-testid={`mentor-${i}`}>
                <Grid cols={3}>
                  <Field label="Mentor name">
                    <input className="input" data-testid={`mentor-${i}-name`} value={m.name || ""} onChange={(e) => updMentor(i, "name", e.target.value)} placeholder="e.g. Charley Toomey" />
                  </Field>
                  <Field label="Role">
                    <input className="input" data-testid={`mentor-${i}-role`} value={m.role || ""} onChange={(e) => updMentor(i, "role", e.target.value)} placeholder="Head Coach" />
                  </Field>
                  <Field label="Program / Organization">
                    <input className="input" data-testid={`mentor-${i}-org`} value={m.organization || ""} onChange={(e) => updMentor(i, "organization", e.target.value)} placeholder="Loyola Maryland" />
                  </Field>
                  <Field label="Competition Level">
                    <select className="input" data-testid={`mentor-${i}-level`} value={m.level || ""} onChange={(e) => updMentor(i, "level", e.target.value)}>
                      <option value="">—</option>
                      {MENTOR_LEVELS.map((l) => <option key={l}>{l}</option>)}
                    </select>
                  </Field>
                  <Field label="Program Tier">
                    <select className="input" data-testid={`mentor-${i}-tier`} value={m.tier || ""} onChange={(e) => updMentor(i, "tier", e.target.value)}>
                      <option value="">—</option>
                      {MENTOR_TIERS.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                    </select>
                  </Field>
                  <Field label="Championships (#)">
                    <input className="input" type="number" min="0" max="50" data-testid={`mentor-${i}-champs`} value={m.championships ?? 0} onChange={(e) => updMentor(i, "championships", parseInt(e.target.value || 0))} />
                  </Field>
                  <Field label="Years under this mentor">
                    <input className="input" type="number" min="0" max="40" data-testid={`mentor-${i}-years`} value={m.years_under ?? ""} onChange={(e) => updMentor(i, "years_under", e.target.value ? parseInt(e.target.value) : null)} />
                  </Field>
                  <Field label="HOF coach"><Toggle on={!!m.hof} onChange={(v) => updMentor(i, "hof", v)} testid={`mentor-${i}-hof`} /></Field>
                  <Field label="Won national title"><Toggle on={!!m.national_champion} onChange={(v) => updMentor(i, "national_champion", v)} testid={`mentor-${i}-nat`} /></Field>
                  <Field label="I was direct assistant"><Toggle on={!!m.as_assistant} onChange={(v) => updMentor(i, "as_assistant", v)} testid={`mentor-${i}-asst`} /></Field>
                </Grid>
                <button onClick={() => delMentor(i)} data-testid={`del-mentor-${i}`} className="mt-2 flex items-center gap-1 text-xs text-blaze hover:text-red-400 uppercase tracking-widest">
                  <Trash2 size={14} /> Remove mentor
                </button>
              </div>
            ))}
            {(p.mentors || []).length < 10 && (
              <button onClick={addMentor} data-testid="add-mentor-btn" className="w-full border border-dashed border-neutral-700 py-3 heading text-base uppercase tracking-widest text-volt hover:bg-volt/10 transition-colors flex items-center justify-center gap-2">
                <Plus size={16} /> Add mentor ({(p.mentors || []).length}/10)
              </button>
            )}
          </div>
        </Section>

        <Section title="FACILITIES THAT MATTER TO ME">
          <p className="text-xs text-neutral-500 -mt-2">
            Rate how important each facility is to your decision. <b className="text-volt">1</b> = nice-to-have ·
            <b className="text-volt"> 2</b> = important · <b className="text-volt">3</b> = must-have ·
            <b className="text-neutral-600"> 0</b> = doesn&apos;t matter. Jobs that don&apos;t check your boxes
            still surface, but they score lower so what you actually want bubbles to the top.
          </p>
          <div className="space-y-1" data-testid="facility-priorities">
            <FacPriRow label="Practice Field · Turf" k="practice_field_turf" v={p.facility_priorities?.practice_field_turf || 0} set={(n) => uNested("facility_priorities", "practice_field_turf", n)} />
            <FacPriRow label="Practice Field · Grass" k="practice_field_grass" v={p.facility_priorities?.practice_field_grass || 0} set={(n) => uNested("facility_priorities", "practice_field_grass", n)} />
            <FacPriRow label="Game Field · Turf" k="game_field_turf" v={p.facility_priorities?.game_field_turf || 0} set={(n) => uNested("facility_priorities", "game_field_turf", n)} />
            <FacPriRow label="Game Field · Grass" k="game_field_grass" v={p.facility_priorities?.game_field_grass || 0} set={(n) => uNested("facility_priorities", "game_field_grass", n)} />
            <FacPriRow label="Indoor Facility" k="indoor_facility" v={p.facility_priorities?.indoor_facility || 0} set={(n) => uNested("facility_priorities", "indoor_facility", n)} />
            <FacPriRow label="Weight Room" k="weight_room" v={p.facility_priorities?.weight_room || 0} set={(n) => uNested("facility_priorities", "weight_room", n)} />
            <FacPriRow label="Locker Room" k="locker_room" v={p.facility_priorities?.locker_room || 0} set={(n) => uNested("facility_priorities", "locker_room", n)} />
            <FacPriRow label="Other Facilities (training room, film lab, recovery)" k="other_facilities" v={p.facility_priorities?.other_facilities || 0} set={(n) => uNested("facility_priorities", "other_facilities", n)} />
          </div>
        </Section>

        <Section title="HEAD COACH? HIRE YOUR OWN ASSISTANTS">
          <p className="text-xs text-neutral-500 -mt-2">
            If you&apos;re the sitting Head Coach of a program you can post Assistant-tier roles directly
            without needing an Athletic Director seat. The post is attached to your program and
            limited to assistant-only roles. Free while your AD&apos;s subscription is active.
          </p>
          <Grid cols={2}>
            <Field label="Enable Head Coach Searcher mode">
              <Toggle
                on={!!p.hc_searcher?.enabled}
                onChange={(v) => uNested("hc_searcher", "enabled", v)}
                testid="toggle-hc-searcher"
              />
            </Field>
            <Field label="Your program name (e.g. Duke University Men's Lacrosse)">
              <input className="input" data-testid="hc-program-name" value={p.hc_searcher?.program_name || ""} onChange={(e) => uNested("hc_searcher", "program_name", e.target.value)} />
            </Field>
            <Field label="Program Level">
              <select className="input" data-testid="hc-program-level" value={p.hc_searcher?.level || ""} onChange={(e) => uNested("hc_searcher", "level", e.target.value)}>
                <option value="">—</option>{LEVELS.map((l) => <option key={l}>{l}</option>)}
              </select>
            </Field>
            <Field label="Program Division">
              <select className="input" data-testid="hc-program-division" value={p.hc_searcher?.division || ""} onChange={(e) => uNested("hc_searcher", "division", e.target.value)}>
                <option value="">—</option>{DIVISIONS.map((d) => <option key={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Program Location (City, State)">
              <input className="input" data-testid="hc-program-location" value={p.hc_searcher?.location || ""} onChange={(e) => uNested("hc_searcher", "location", e.target.value)} placeholder="Durham, NC" />
            </Field>
          </Grid>
        </Section>

        <Section title="SPECIALTY & DEPARTURE CONDITIONS">
          <p className="text-xs text-neutral-500 -mt-2">What you specialize in and what would make you leave your current position — visible to ADs ONLY after you express interest in their job.</p>
          <Field label="Coaching specialties (pick all that apply)">
            <p className="text-[11px] text-neutral-500 mb-2">
              Pick every hat you wear. A coach can be both an Offensive Coordinator
              AND a Goalie coach — both will surface in the AD&apos;s Talent Pool
              position filter.
            </p>
            <div className="flex flex-wrap gap-2" data-testid="specialty-tags">
              {[
                "Head Coach",
                "Assistant Coach",
                "Offensive Coordinator",
                "Defensive Coordinator",
                "Goalie Coach",
                "Face-off Coach",
                "Recruiting Coordinator",
                "Strength & Conditioning",
                "Player Development",
                "Offense",
                "Defense",
                "Midfield",
                "Attack",
                "Outreach & Admit Cycles",
                "Game Management",
                "Mental Performance",
              ].map((s) => {
                const on = (p.specialties || []).includes(s);
                return (
                  <button
                    type="button"
                    key={s}
                    data-testid={`specialty-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => u("specialties", on ? p.specialties.filter((x) => x !== s) : [...(p.specialties || []), s])}
                    className={`px-3 min-h-[36px] text-xs tracking-widest border transition-colors ${on ? "bg-volt text-white border-volt" : "border-neutral-700 text-neutral-300 hover:border-volt"}`}
                  >
                    {s.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="What it would take for you to leave your current position">
            <textarea
              data-testid="departure-conditions"
              className="input min-h-[80px]"
              value={p.departure_conditions || ""}
              onChange={(e) => u("departure_conditions", e.target.value)}
              placeholder="e.g. Head Coach title at D1+ program, $X+ base salary, full scholarship roster, located within 3 hrs of family in NJ. Will not consider any role without a clear path to championship-contention budget."
            />
          </Field>
        </Section>

        {(!p.primary_level || p.primary_level === "HS" || p.primary_level === "Either" || p.primary_level === "Any") && (
          <Section title="HS COACH + FACULTY (HIGH-SCHOOL ROLES ONLY)">
            <p className="text-xs text-neutral-500 -mt-2">Most HS coaching jobs come paired with classroom teaching — a full-time HS coaching role is almost always coach + teacher. Tell us what your <b className="text-neutral-300">degree qualifies you to teach</b> so we can match you accurately. Not used for college / club / middle-school searches.</p>

            <Field label="Position type preference (HS roles are often coach + teacher)">
              <div className="flex flex-wrap gap-2" data-testid="hs-position-type-tags">
                {[
                  { v: "full_time", label: "Full-time only (coach + teacher)" },
                  { v: "part_time", label: "Part-time only (coach only)" },
                  { v: "either", label: "Either" },
                ].map((opt) => {
                  const on = (p.position_type_pref || "either") === opt.v;
                  return (
                    <button
                      type="button"
                      key={opt.v}
                      data-testid={`hs-position-type-${opt.v}`}
                      onClick={() => u("position_type_pref", opt.v)}
                      className={`px-3 min-h-[36px] text-xs tracking-widest border transition-colors ${on ? "bg-volt text-white border-volt" : "border-neutral-700 text-neutral-300 hover:border-volt"}`}
                    >
                      {opt.label.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label="Are you open to a hybrid Coach + Faculty role (teach + coach)?">
              <div className="flex gap-2" data-testid="open-to-faculty-toggle">
                {[
                  { v: true, label: "Yes — I'd teach + coach" },
                  { v: false, label: "No — coaching only" },
                ].map((opt) => {
                  const on = !!p.open_to_faculty === opt.v;
                  return (
                    <button
                      type="button"
                      key={String(opt.v)}
                      data-testid={`faculty-${opt.v ? "yes" : "no"}`}
                      onClick={() => u("open_to_faculty", opt.v)}
                      className={`px-3 min-h-[36px] text-xs tracking-widest border transition-colors ${on ? "bg-volt text-white border-volt" : "border-neutral-700 text-neutral-300 hover:border-volt"}`}
                    >
                      {opt.label.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </Field>

            {p.open_to_faculty && (
              <Field label="Subject(s) your degree qualifies you to teach (pick all that apply — multiple selections OK)">
                <p className="text-[11px] text-neutral-500 -mt-2 mb-2">
                  Only include subjects you have the degree / certification / practical qualification to teach. This drives which full-time HS jobs match you.
                </p>
                <div className="flex flex-wrap gap-2" data-testid="faculty-subjects-tags">
                  {["Math", "Science", "English", "Social Studies", "Language", "Fine Arts", "Computer Science", "Physical Education", "Counselor", "School Administration", "Other"].map((s) => {
                    const on = (p.faculty_subjects || []).includes(s);
                    return (
                      <button
                        type="button"
                        key={s}
                        data-testid={`faculty-subject-${s.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => u("faculty_subjects", on ? p.faculty_subjects.filter((x) => x !== s) : [...(p.faculty_subjects || []), s])}
                        className={`px-3 min-h-[36px] text-xs tracking-widest border transition-colors ${on ? "bg-volt text-white border-volt" : "border-neutral-700 text-neutral-300 hover:border-volt"}`}
                      >
                        {s.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              </Field>
            )}
          </Section>
        )}

        <button onClick={save} disabled={busy} data-testid="save-profile-btn-bottom" className="w-full mt-8 bg-blaze text-white heading text-xl py-4 min-h-[56px] hover:glow-blaze transition-all disabled:opacity-50">
          {busy ? "SAVING…" : "SAVE & MATCH ME"}
        </button>

        <AccountEmailSection profileEmail={p.email} />
      </div>
      <style>{`.input { width: 100%; background: #121212; border: 1px solid #27272A; padding: 10px 12px; color: white; outline: none; font-size: 13px; }
      .input:focus { border-color: #FF3B30; }`}</style>
    </Shell>
  );
}

function AccountEmailSection({ profileEmail }) {
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy2, setBusy2] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  const submit = async () => {
    setBusy2(true);
    try {
      const { data } = await api.post("/auth/change-email", { new_email: newEmail, password });
      toast.success(data.detail);
      setSentTo(newEmail);
      setPassword("");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't start the email change.");
    } finally {
      setBusy2(false);
    }
  };

  return (
    <div className="mt-8 border border-neutral-800 p-6 bg-appcard" data-testid="account-email-section">
      <div className="text-xs tracking-widest text-volt mb-2">ACCOUNT EMAIL</div>
      <p className="text-xs text-neutral-400 leading-relaxed mb-4">
        Claimed with your work email? Switch your login to a personal address any time — we'll send a
        confirmation link to the new inbox, and nothing changes until you click it. All alerts and match
        notifications move with it.
      </p>
      {sentTo ? (
        <div className="border border-volt/40 bg-volt/5 p-4 text-sm text-neutral-200" data-testid="account-email-sent-note">
          Confirmation link sent to <b className="text-volt">{sentTo}</b>. Open that inbox and click the link to finish the switch.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="email"
            className="input"
            placeholder="new personal email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            data-testid="account-new-email-input"
          />
          <input
            type="password"
            className="input"
            placeholder="current password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="account-email-password-input"
          />
          <button
            onClick={submit}
            disabled={busy2 || !newEmail || !password}
            data-testid="account-email-submit-btn"
            className="min-h-[42px] border border-volt text-volt heading text-sm tracking-widest px-4 hover:bg-volt hover:text-white transition-all disabled:opacity-40"
          >
            {busy2 ? "SENDING…" : "CHANGE MY EMAIL"}
          </button>
        </div>
      )}
      {profileEmail && !sentTo && (
        <div className="text-[11px] text-neutral-500 mt-3">Current contact email on file: {profileEmail}</div>
      )}
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <div className="mt-8 border border-neutral-800 p-6 bg-appcard">
      <div className="text-xs tracking-widest text-volt">{title}</div>
      {hint && <p className="text-xs text-neutral-500 mt-1.5 mb-4 leading-relaxed max-w-3xl">{hint}</p>}
      {!hint && <div className="mb-4" />}
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Grid({ cols = 2, children }) { return <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-3`}>{children}</div>; }
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-widest text-neutral-500 mb-1.5">{label.toUpperCase()}</div>
      {children}
    </label>
  );
}
function ChipRow({ options, value, onToggle, testid, single }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <button
            key={o}
            type="button"
            data-testid={`${testid}-${o.replace(/[^a-z0-9]/gi, "-")}`}
            onClick={() => onToggle(o)}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${
              on ? "bg-blaze border-blaze text-white" : "border-neutral-700 text-neutral-300 hover:border-volt"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}
function Toggle({ on, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!on)} className={`h-9 px-4 text-xs uppercase tracking-widest border ${on ? "bg-volt border-volt text-white" : "border-neutral-700 text-neutral-400"}`}>{on ? "YES" : "NO"}</button>
  );
}

function FacPriRow({ label, k, v, set }) {
  const LABELS = { 0: "—", 1: "NICE", 2: "IMPORTANT", 3: "MUST-HAVE" };
  return (
    <div className="grid grid-cols-12 gap-3 items-center py-2 border-b border-neutral-900" data-testid={`fac-pri-${k}`}>
      <div className="col-span-12 md:col-span-6 text-sm text-neutral-200">{label}</div>
      <div className="col-span-12 md:col-span-6 flex gap-px bg-neutral-800">
        {[0, 1, 2, 3].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`fac-pri-${k}-${n}`}
            onClick={() => set(n)}
            className={`flex-1 py-2 text-[10px] tracking-widest uppercase transition-colors ${v === n ? (n === 0 ? "bg-neutral-700 text-white" : "bg-volt text-white") : "bg-appcard text-neutral-500 hover:text-white"}`}
          >
            {n}{n > 0 ? <span className="hidden md:inline"> · {LABELS[n]}</span> : ""}
          </button>
        ))}
      </div>
    </div>
  );
}
function Shell({ children }) {
  return <div className="min-h-screen bg-appbg text-white"><Header />{children}</div>;
}

// --------------------------------------------------------------------------
// Per-season W-L-T editor — drives the new "Program Growth Trajectory" CIR
// component. Adding 3+ seasons of consecutive improvement awards bonus pts.
// --------------------------------------------------------------------------
function SeasonRecordsEditor({ stintIdx, seasons, onChange }) {
  const add = () => {
    const lastYear = seasons.length ? (seasons[seasons.length - 1].year || new Date().getFullYear()) : new Date().getFullYear();
    onChange([...seasons, { year: lastYear + 1, wins: 0, losses: 0, ties: 0, postseason: "", team_accolades: [], personal_accolades: [] }]);
  };
  const upd = (i, k, v) => {
    const arr = [...seasons];
    arr[i] = { ...arr[i], [k]: v };
    onChange(arr);
  };
  const del = (i) => onChange(seasons.filter((_, j) => j !== i));

  // Compute growth signal locally so coach sees real-time feedback as they enter data
  const sorted = [...seasons].filter((s) => s.year).sort((a, b) => a.year - b.year);
  const winPcts = sorted.map((s) => {
    const total = (s.wins || 0) + (s.losses || 0) + (s.ties || 0);
    return total ? (((s.wins || 0) + (s.ties || 0) * 0.5) / total) : 0;
  });
  let bestConsec = 1, c = 1;
  for (let k = 1; k < winPcts.length; k++) {
    if (winPcts[k] > winPcts[k - 1] + 0.001) { c++; bestConsec = Math.max(bestConsec, c); }
    else c = 1;
  }
  const growthSignal = winPcts.length >= 3 && winPcts[winPcts.length - 1] > winPcts[0]
    ? bestConsec >= 3
      ? "🔥 Constant-growth bonus active — last few seasons strictly improving"
      : "📈 Upward trend detected (positive CIR growth signal)"
    : winPcts.length < 3 ? "Add at least 3 seasons to trigger the growth bonus" : "Mixed trend — flat or down across seasons";

  return (
    <div className="mt-4 border border-neutral-800 bg-[#070707] p-3 rounded-sm" data-testid={`stint-${stintIdx}-seasons`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-widest text-volt">SEASON-BY-SEASON RECORDS</div>
        <div className="text-[10px] text-neutral-500 italic">{growthSignal}</div>
      </div>
      <div className="space-y-1">
        {sorted.length === 0 && <div className="text-xs text-neutral-500 px-1">No season rows yet. Add one below to start tracking growth.</div>}
        {/* Header row */}
        {sorted.length > 0 && (
          <div className="grid grid-cols-12 gap-2 text-[10px] tracking-widest text-neutral-500 px-1">
            <div className="col-span-1">YEAR</div>
            <div className="col-span-1">W</div>
            <div className="col-span-1">L</div>
            <div className="col-span-1">T</div>
            <div className="col-span-3">POSTSEASON</div>
            <div className="col-span-2">TEAM ACCOLADES</div>
            <div className="col-span-2">PERSONAL ACC.</div>
            <div className="col-span-1"></div>
          </div>
        )}
        {seasons.map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-2" data-testid={`stint-${stintIdx}-season-${i}`}>
            <input type="number" className="input col-span-1 text-xs" value={s.year || ""} onChange={(e) => upd(i, "year", parseInt(e.target.value || 0))} placeholder="2024" />
            <input type="number" className="input col-span-1 text-xs" value={s.wins ?? 0} onChange={(e) => upd(i, "wins", parseInt(e.target.value || 0))} />
            <input type="number" className="input col-span-1 text-xs" value={s.losses ?? 0} onChange={(e) => upd(i, "losses", parseInt(e.target.value || 0))} />
            <input type="number" className="input col-span-1 text-xs" value={s.ties ?? 0} onChange={(e) => upd(i, "ties", parseInt(e.target.value || 0))} />
            <input type="text" className="input col-span-3 text-xs" value={s.postseason || ""} onChange={(e) => upd(i, "postseason", e.target.value)} placeholder="e.g., NCAA 2nd Round" />
            <input type="text" className="input col-span-2 text-xs" value={(s.team_accolades || []).join(", ")} onChange={(e) => upd(i, "team_accolades", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="Conf champs" />
            <input type="text" className="input col-span-2 text-xs" value={(s.personal_accolades || []).join(", ")} onChange={(e) => upd(i, "personal_accolades", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="CoY" />
            <button type="button" onClick={() => del(i)} data-testid={`stint-${stintIdx}-season-${i}-del`} className="col-span-1 text-blaze hover:text-red-400 text-xs flex items-center justify-center">✕</button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} data-testid={`stint-${stintIdx}-add-season`} className="mt-2 text-[10px] tracking-widest text-volt hover:text-white border border-dashed border-volt/40 px-3 py-1.5 w-full uppercase">
        + Add a season row
      </button>
      {sorted.length >= 2 && (
        <div className="mt-2 text-[11px] text-neutral-400 px-1">
          Career sum from seasons: <span className="text-mono text-white">{sorted.reduce((a, s) => a + (s.wins || 0), 0)}-{sorted.reduce((a, s) => a + (s.losses || 0), 0)}</span>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Notable players coached — feeds CIR storytelling + AD-side context.
// --------------------------------------------------------------------------
function NotablePlayersEditor({ stintIdx, players, onChange }) {
  const add = () => onChange([...players, { name: "", position: "", graduation_year: null, destination: "", destination_type: "", accolade: "" }]);
  const upd = (i, k, v) => {
    const arr = [...players];
    arr[i] = { ...arr[i], [k]: v };
    onChange(arr);
  };
  const del = (i) => onChange(players.filter((_, j) => j !== i));

  const DEST_TYPES = ["", "D1", "D2", "D3", "MCLA", "WCLA", "JUCO", "PLL", "MLL", "Olympic", "Pro Other"];

  return (
    <div className="mt-4 border border-neutral-800 bg-[#070707] p-3 rounded-sm" data-testid={`stint-${stintIdx}-players`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-widest text-volt">NOTABLE PLAYERS COACHED</div>
        <div className="text-[10px] text-neutral-500 italic">Significant players you helped develop and where they went next</div>
      </div>
      <div className="space-y-1">
        {players.length === 0 && <div className="text-xs text-neutral-500 px-1">No notable players logged yet. These give ADs concrete proof of your player-development track record.</div>}
        {players.length > 0 && (
          <div className="grid grid-cols-12 gap-2 text-[10px] tracking-widest text-neutral-500 px-1">
            <div className="col-span-3">PLAYER NAME</div>
            <div className="col-span-1">POS</div>
            <div className="col-span-1">GRAD</div>
            <div className="col-span-3">DESTINATION</div>
            <div className="col-span-1">TYPE</div>
            <div className="col-span-2">ACCOLADE</div>
            <div className="col-span-1"></div>
          </div>
        )}
        {players.map((pl, i) => (
          <div key={i} className="grid grid-cols-12 gap-2" data-testid={`stint-${stintIdx}-player-${i}`}>
            <input type="text" className="input col-span-3 text-xs" value={pl.name || ""} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Player Name" />
            <input type="text" className="input col-span-1 text-xs" value={pl.position || ""} onChange={(e) => upd(i, "position", e.target.value)} placeholder="A/M/D/G" />
            <input type="number" className="input col-span-1 text-xs" value={pl.graduation_year || ""} onChange={(e) => upd(i, "graduation_year", e.target.value ? parseInt(e.target.value) : null)} placeholder="2024" />
            <input type="text" className="input col-span-3 text-xs" value={pl.destination || ""} onChange={(e) => upd(i, "destination", e.target.value)} placeholder="Duke" />
            <select className="input col-span-1 text-xs" value={pl.destination_type || ""} onChange={(e) => upd(i, "destination_type", e.target.value)}>
              {DEST_TYPES.map((t) => <option key={t} value={t}>{t || "—"}</option>)}
            </select>
            <input type="text" className="input col-span-2 text-xs" value={pl.accolade || ""} onChange={(e) => upd(i, "accolade", e.target.value)} placeholder="All-American 2023" />
            <button type="button" onClick={() => del(i)} data-testid={`stint-${stintIdx}-player-${i}-del`} className="col-span-1 text-blaze hover:text-red-400 text-xs flex items-center justify-center">✕</button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} data-testid={`stint-${stintIdx}-add-player`} className="mt-2 text-[10px] tracking-widest text-volt hover:text-white border border-dashed border-volt/40 px-3 py-1.5 w-full uppercase">
        + Add a notable player
      </button>
    </div>
  );
}
